import React, { useEffect, useRef, useState } from "react";
import {
  Card,
  Button,
  Modal,
  Form,
  InputGroup,
  Row,
  Col,
  Spinner,
  ProgressBar,
  Nav,
  Tab,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileAlt,
  faFilePdf,
  faFileImage,
  faFileCsv,
  faFileWord,
  faFileExcel,
  faFilePowerpoint,
  faFileCode,
  faFileArchive,
  faFile,
  faPlus,
  faTrash,
  faExpand,
  faUpload,
  faDownload,
  faLink,
  faExternalLinkAlt,
  faEdit,
  faGlobe,
} from "@fortawesome/free-solid-svg-icons";
import "./QuillEditor.css"; // Reusing the same CSS file for styling
import { useAuth } from "../context/AuthContext";
import toast from "../utils/toastUtils";

// File and link service functions
import {
  uploadFile,
  getMeetingFiles,
  deleteFile,
  getFileDataUrl,
} from "../api/fileService";
import {
  getMeetingLinks,
  addLink,
  updateLink,
  deleteLink,
} from "../api/linkService";

const FileAttachments = ({ meetingId }) => {
  const { currentUser } = useAuth();
  const fileInputRef = useRef(null);

  // Shared state
  const [activeTab, setActiveTab] = useState("files");

  // File attachment states
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [showFileModal, setShowFileModal] = useState(false);
  const [fileToUpload, setFileToUpload] = useState(null);
  const [fileDescription, setFileDescription] = useState("");
  const [fileError, setFileError] = useState("");
  const [viewModal, setViewModal] = useState(false);
  const [currentViewFile, setCurrentViewFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);

  // Link attachment states
  const [attachedLinks, setAttachedLinks] = useState([]);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkTitle, setLinkTitle] = useState("");
  const [linkDescription, setLinkDescription] = useState("");
  const [linkError, setLinkError] = useState("");
  const [addingLink, setAddingLink] = useState(false);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [editingLinkId, setEditingLinkId] = useState(null);

  // Fetch files and links when meeting ID changes
  useEffect(() => {
    if (meetingId) {
      fetchMeetingFiles();
      fetchMeetingLinks();
    }
  }, [meetingId]);

  // Function to fetch meeting files
  const fetchMeetingFiles = async () => {
    if (!meetingId) return;

    try {
      setLoadingFiles(true);
      const files = await getMeetingFiles(meetingId);
      setAttachedFiles(files);
    } catch (error) {
      console.error("Error fetching meeting files:", error);
      toast.error("Failed to load attached files");
    } finally {
      setLoadingFiles(false);
    }
  };

  // Function to fetch meeting links
  const fetchMeetingLinks = async () => {
    if (!meetingId) return;

    try {
      setLoadingLinks(true);
      const links = await getMeetingLinks(meetingId);
      setAttachedLinks(links);
    } catch (error) {
      console.error("Error fetching meeting links:", error);
      toast.error("Failed to load attached links");
    } finally {
      setLoadingLinks(false);
    }
  };

  // FILE HANDLING FUNCTIONS

  // Function to handle file selection for upload
  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFileToUpload(selectedFile);
      // Clear any previous errors
      setFileError("");
    }
  };

  // Function to handle file upload
  const handleFileUpload = async () => {
    if (!fileToUpload) {
      setFileError("Please select a file to upload");
      return;
    }

    if (!meetingId) {
      setFileError("Meeting ID is required for file upload");
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      // Create a simulated upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          const newProgress = prev + Math.random() * 20;
          return newProgress > 90 ? 90 : newProgress;
        });
      }, 500);

      // Perform the actual upload
      const result = await uploadFile(
        fileToUpload,
        meetingId,
        currentUser?.name || "Anonymous",
        fileDescription
      );

      // Clear the interval and set progress to 100%
      clearInterval(progressInterval);
      setUploadProgress(100);

      // Add the new file to the list
      setAttachedFiles([...attachedFiles, result.file]);

      toast.success("File uploaded successfully");

      // Reset form and close modal
      setFileToUpload(null);
      setFileDescription("");
      setShowFileModal(false);

      // Fetch updated files to ensure we have the latest
      fetchMeetingFiles();
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error(
        "Failed to upload file: " + (error.message || "Unknown error")
      );
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Function to remove a file
  const handleRemoveFile = async (fileId) => {
    try {
      await deleteFile(fileId);
      setAttachedFiles(attachedFiles.filter((file) => file._id !== fileId));
      toast.info("File removed");
    } catch (error) {
      console.error("Error removing file:", error);
      toast.error("Failed to remove file");
    }
  };

  // Function to view a file in the modal
  const handleViewFile = (file) => {
    setCurrentViewFile(file);
    setViewModal(true);
  };

  // LINK HANDLING FUNCTIONS

  // Function to handle link submission
  const handleLinkSubmit = async () => {
    // Validate URL
    if (!linkUrl) {
      setLinkError("Please enter a URL");
      return;
    }

    // Add http:// if missing
    let formattedUrl = linkUrl;
    if (!/^https?:\/\//i.test(formattedUrl)) {
      formattedUrl = "https://" + formattedUrl;
    }

    try {
      setAddingLink(true);

      if (editingLinkId) {
        // Update existing link
        await updateLink(editingLinkId, {
          url: formattedUrl,
          title: linkTitle || new URL(formattedUrl).hostname,
          description: linkDescription,
        });
        toast.success("Link updated successfully");
      } else {
        // Add new link
        await addLink(meetingId, {
          url: formattedUrl,
          title: linkTitle || new URL(formattedUrl).hostname,
          description: linkDescription,
          addedBy: currentUser?.name || "Anonymous",
        });
        toast.success("Link added successfully");
      }

      // Reset form and close modal
      resetLinkForm();
      setShowLinkModal(false);

      // Refresh the links
      fetchMeetingLinks();
    } catch (error) {
      console.error("Error with link:", error);
      setLinkError(
        "Failed to process link: " + (error.message || "Unknown error")
      );
    } finally {
      setAddingLink(false);
    }
  };

  // Function to edit a link
  const handleEditLink = (link) => {
    setLinkUrl(link.url);
    setLinkTitle(link.title);
    setLinkDescription(link.description || "");
    setEditingLinkId(link._id);
    setShowLinkModal(true);
  };

  // Function to remove a link
  const handleRemoveLink = async (linkId) => {
    try {
      await deleteLink(linkId);
      setAttachedLinks(attachedLinks.filter((link) => link._id !== linkId));
      toast.info("Link removed");
    } catch (error) {
      console.error("Error removing link:", error);
      toast.error("Failed to remove link");
    }
  };

  // Reset link form
  const resetLinkForm = () => {
    setLinkUrl("");
    setLinkTitle("");
    setLinkDescription("");
    setLinkError("");
    setEditingLinkId(null);
  };

  // UTILITY FUNCTIONS

  // Function to get file icon based on type
  const getFileIcon = (contentType) => {
    if (contentType.includes("image")) {
      return faFileImage;
    } else if (contentType.includes("pdf")) {
      return faFilePdf;
    } else if (contentType.includes("csv")) {
      return faFileCsv;
    } else if (
      contentType.includes("msword") ||
      contentType.includes("wordprocessingml")
    ) {
      return faFileWord;
    } else if (
      contentType.includes("spreadsheetml") ||
      contentType.includes("excel")
    ) {
      return faFileExcel;
    } else if (
      contentType.includes("presentationml") ||
      contentType.includes("powerpoint")
    ) {
      return faFilePowerpoint;
    } else if (
      contentType.includes("javascript") ||
      contentType.includes("html") ||
      contentType.includes("css")
    ) {
      return faFileCode;
    } else if (
      contentType.includes("zip") ||
      contentType.includes("rar") ||
      contentType.includes("tar") ||
      contentType.includes("gzip")
    ) {
      return faFileArchive;
    } else {
      return faFileAlt;
    }
  };

  // Get file type name for display
  const getFileTypeName = (contentType) => {
    if (contentType.includes("image")) return "Image";
    if (contentType.includes("pdf")) return "PDF";
    if (
      contentType.includes("msword") ||
      contentType.includes("wordprocessingml")
    )
      return "Word";
    if (contentType.includes("spreadsheetml") || contentType.includes("excel"))
      return "Excel";
    if (
      contentType.includes("presentationml") ||
      contentType.includes("powerpoint")
    )
      return "PowerPoint";
    if (contentType.includes("javascript")) return "JavaScript";
    if (contentType.includes("html")) return "HTML";
    if (contentType.includes("css")) return "CSS";
    if (contentType.includes("csv")) return "CSV";
    if (
      contentType.includes("zip") ||
      contentType.includes("rar") ||
      contentType.includes("tar")
    )
      return "Archive";

    return contentType.split("/")[1]?.toUpperCase() || "File";
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " bytes";
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    else return (bytes / 1048576).toFixed(1) + " MB";
  };

  // Render file preview based on content type
  const renderFilePreview = (file) => {
    const fileUrl = getFileDataUrl(file._id);

    if (file.contentType.includes("image")) {
      return (
        <img
          src={fileUrl}
          alt={file.metadata.originalName}
          className="img-fluid view-file"
        />
      );
    } else if (file.contentType.includes("pdf")) {
      return (
        <div className="pdf-embed-container">
          <iframe
            src={`${fileUrl}#view=FitH`}
            title={file.metadata.originalName}
            className="pdf-viewer"
          />
        </div>
      );
    } else {
      return (
        <div className="file-preview-fallback">
          <FontAwesomeIcon
            icon={getFileIcon(file.contentType)}
            size="5x"
            className="mb-3"
          />
          <h4>{file.metadata.originalName}</h4>
          <p>
            Type: {file.contentType}
            <br />
            Size: {formatFileSize(file.size)}
          </p>
          <p>
            Preview not available. Click the download button to access this
            file.
          </p>
        </div>
      );
    }
  };

  return (
    <Card className="shadow-sm border-0 mb-4 file-attachments-component">
      <Card.Header className="bg-white border-bottom-0 d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Attachments</h5>
        <div>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setActiveTab("files")}
            className={`me-2 ${activeTab === "files" ? "active" : ""}`}
          >
            <FontAwesomeIcon icon={faFileAlt} className="me-1" />
            Files
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setActiveTab("links")}
            className={activeTab === "links" ? "active" : ""}
          >
            <FontAwesomeIcon icon={faLink} className="me-1" />
            Links
          </Button>
        </div>
      </Card.Header>
      <Card.Body className="pt-0">
        <Tab.Container activeKey={activeTab}>
          <Tab.Content>
            {/* Files Tab */}
            <Tab.Pane eventKey="files">
              <div className="file-attachments-section">
                {loadingFiles ? (
                  <div className="text-center p-3">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading files...
                  </div>
                ) : (
                  <>
                    {attachedFiles.length > 0 && (
                      <>
                        <Row className="g-2">
                          {attachedFiles.map((file) => (
                            <Col key={file._id} xs={6} md={4} lg={6}>
                              <Card className="file-attachment-card">
                                <div className="file-thumb-container">
                                  {file.contentType.includes("image") ? (
                                    <Card.Img
                                      variant="top"
                                      src={getFileDataUrl(file._id)}
                                      onClick={() => handleViewFile(file)}
                                      className="file-thumbnail"
                                      alt={file.metadata.originalName}
                                    />
                                  ) : (
                                    <div
                                      className="file-icon-container"
                                      onClick={() => handleViewFile(file)}
                                    >
                                      <FontAwesomeIcon
                                        icon={getFileIcon(file.contentType)}
                                        className="file-icon"
                                      />
                                    </div>
                                  )}
                                </div>
                                <Card.Body className="p-2">
                                  <div
                                    className="file-title text-truncate mb-1"
                                    title={file.metadata.originalName}
                                    onClick={() => handleViewFile(file)}
                                  >
                                    {file.metadata.originalName}
                                  </div>
                                  <div className="file-metadata">
                                    <small>
                                      {getFileTypeName(file.contentType)} ·{" "}
                                      {formatFileSize(file.size)}
                                    </small>
                                  </div>
                                  <div className="d-flex justify-content-between mt-2">
                                    <a
                                      href={getFileDataUrl(file._id)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="attachment-btn"
                                      title="Download file"
                                    >
                                      <FontAwesomeIcon icon={faDownload} />
                                    </a>
                                    <Button
                                      variant="link"
                                      size="sm"
                                      onClick={() => handleRemoveFile(file._id)}
                                      className="attachment-btn text-danger"
                                      title="Remove file"
                                    >
                                      <FontAwesomeIcon icon={faTrash} />
                                    </Button>
                                  </div>
                                </Card.Body>
                              </Card>
                            </Col>
                          ))}
                          {/* Add File Button */}
                          <Col xs={6} md={4} lg={6}>
                            <Card
                              className="add-file-card"
                              onClick={() => setShowFileModal(true)}
                            >
                              <div className="add-file-container d-flex align-items-center justify-content-center">
                                <FontAwesomeIcon
                                  icon={faPlus}
                                  className="add-file-icon"
                                />
                                <div className="mt-2">Add File</div>
                              </div>
                            </Card>
                          </Col>
                        </Row>
                      </>
                    )}

                    {/* Show "Add File" button if no files are attached */}
                    {attachedFiles.length === 0 && (
                      <div className="text-center py-4">
                        <FontAwesomeIcon
                          icon={faFileAlt}
                          className="empty-state-icon mb-2"
                        />
                        <p className="mb-3">No files attached yet</p>
                        <Button
                          variant="outline-primary"
                          onClick={() => setShowFileModal(true)}
                        >
                          <FontAwesomeIcon icon={faPlus} className="me-2" />
                          Add File
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </Tab.Pane>

            {/* Links Tab */}
            <Tab.Pane eventKey="links">
              <div className="link-attachments-section">
                {loadingLinks ? (
                  <div className="text-center p-3">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading links...
                  </div>
                ) : (
                  <>
                    {attachedLinks.length > 0 && (
                      <Row className="g-2">
                        {attachedLinks.map((link) => (
                          <Col key={link._id} xs={12}>
                            <Card className="link-card">
                              <Card.Body className="p-2">
                                <div className="d-flex">
                                  <div className="link-icon-container me-2">
                                    <FontAwesomeIcon
                                      icon={faGlobe}
                                      className="link-icon"
                                    />
                                  </div>
                                  <div className="link-content">
                                    <h6 className="mb-1 link-title">
                                      {link.title}
                                    </h6>
                                    <a
                                      href={link.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="link-url"
                                    >
                                      {link.url.replace(/^https?:\/\//, "")}
                                      <FontAwesomeIcon
                                        icon={faExternalLinkAlt}
                                        className="ms-1 external-link-icon"
                                      />
                                    </a>
                                    {link.description && (
                                      <p className="link-description mb-0 mt-1">
                                        {link.description}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <div className="link-actions">
                                  <Button
                                    variant="link"
                                    size="sm"
                                    onClick={() => handleEditLink(link)}
                                    className="link-action-btn"
                                    title="Edit link"
                                  >
                                    <FontAwesomeIcon icon={faEdit} />
                                  </Button>
                                  <Button
                                    variant="link"
                                    size="sm"
                                    onClick={() => handleRemoveLink(link._id)}
                                    className="link-action-btn text-danger"
                                    title="Remove link"
                                  >
                                    <FontAwesomeIcon icon={faTrash} />
                                  </Button>
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>
                        ))}
                        {/* Add Link Button */}
                        <Col xs={12}>
                          <Card
                            className="add-link-card"
                            onClick={() => {
                              resetLinkForm();
                              setShowLinkModal(true);
                            }}
                          >
                            <div className="add-link-container d-flex align-items-center justify-content-center">
                              <FontAwesomeIcon
                                icon={faPlus}
                                className="add-link-icon me-2"
                              />
                              <div>Add Link</div>
                            </div>
                          </Card>
                        </Col>
                      </Row>
                    )}

                    {/* Show "Add Link" button if no links are attached */}
                    {attachedLinks.length === 0 && (
                      <div className="text-center py-4">
                        <FontAwesomeIcon
                          icon={faLink}
                          className="empty-state-icon mb-2"
                        />
                        <p className="mb-3">No links attached yet</p>
                        <Button
                          variant="outline-primary"
                          onClick={() => {
                            resetLinkForm();
                            setShowLinkModal(true);
                          }}
                        >
                          <FontAwesomeIcon icon={faPlus} className="me-2" />
                          Add Link
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </Card.Body>

      {/* Add File Modal */}
      <Modal
        show={showFileModal}
        onHide={() => {
          setShowFileModal(false);
          setFileToUpload(null);
          setFileDescription("");
          setFileError("");
        }}
        className="file-modal"
      >
        <Modal.Header
          closeButton
          style={{
            background: "linear-gradient(to right, #fff0f5, #fff8dc)",
          }}
        >
          <Modal.Title style={{ color: "#d9669b" }}>
            Add File Attachment
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">
            Upload a file to attach to this meeting. Supported file types
            include images, PDFs, documents, spreadsheets, and more.
          </p>

          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Select File</Form.Label>
              <InputGroup>
                <Form.Control
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  isInvalid={!!fileError}
                />
                <Form.Control.Feedback type="invalid">
                  {fileError}
                </Form.Control.Feedback>
              </InputGroup>
              {fileToUpload && (
                <div className="mt-2 text-muted small">
                  Selected file: {fileToUpload.name} (
                  {formatFileSize(fileToUpload.size)})
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={fileDescription}
                onChange={(e) => setFileDescription(e.target.value)}
                placeholder="Enter a description for this file"
              />
            </Form.Group>

            {uploading && (
              <div className="mb-3">
                <Form.Label>Upload Progress</Form.Label>
                <ProgressBar
                  now={uploadProgress}
                  label={`${Math.round(uploadProgress)}%`}
                  variant="success"
                  animated={uploadProgress < 100}
                />
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowFileModal(false);
              setFileToUpload(null);
              setFileDescription("");
              setFileError("");
            }}
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleFileUpload}
            disabled={!fileToUpload || uploading}
            style={{
              background: "linear-gradient(to right, #e66b8f, #d4af37)",
              border: "none",
            }}
          >
            {uploading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Uploading...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faUpload} className="me-2" />
                Upload File
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add/Edit Link Modal */}
      <Modal
        show={showLinkModal}
        onHide={() => {
          setShowLinkModal(false);
          resetLinkForm();
        }}
        className="link-modal"
      >
        <Modal.Header
          closeButton
          style={{
            background: "linear-gradient(to right, #e7f0fd, #f2f6fc)",
          }}
        >
          <Modal.Title style={{ color: "#4a6fdc" }}>
            {editingLinkId ? "Edit Link" : "Add Link"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>URL</Form.Label>
              <InputGroup>
                <Form.Control
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://example.com"
                  isInvalid={!!linkError}
                />
                <Form.Control.Feedback type="invalid">
                  {linkError}
                </Form.Control.Feedback>
              </InputGroup>
              <Form.Text className="text-muted">
                Enter the full URL including https://
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Title (optional)</Form.Label>
              <Form.Control
                type="text"
                value={linkTitle}
                onChange={(e) => setLinkTitle(e.target.value)}
                placeholder="Enter a title for this link"
              />
              <Form.Text className="text-muted">
                If left blank, we'll use the domain name
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={linkDescription}
                onChange={(e) => setLinkDescription(e.target.value)}
                placeholder="Enter a description for this link"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowLinkModal(false);
              resetLinkForm();
            }}
            disabled={addingLink}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleLinkSubmit}
            disabled={!linkUrl || addingLink}
            style={{
              background: "linear-gradient(to right, #4a6fdc, #6f86d6)",
              border: "none",
            }}
          >
            {addingLink ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                {editingLinkId ? "Updating..." : "Adding..."}
              </>
            ) : (
              <>
                <FontAwesomeIcon
                  icon={editingLinkId ? faEdit : faLink}
                  className="me-2"
                />
                {editingLinkId ? "Update Link" : "Add Link"}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* View File Modal */}
      <Modal
        show={viewModal}
        onHide={() => setViewModal(false)}
        size="lg"
        className="view-file-modal"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {currentViewFile?.metadata?.originalName || "File"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {currentViewFile && renderFilePreview(currentViewFile)}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            href={currentViewFile ? getFileDataUrl(currentViewFile._id) : "#"}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FontAwesomeIcon icon={faDownload} className="me-2" />
            Download/Open
          </Button>
          <Button variant="secondary" onClick={() => setViewModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default FileAttachments;
